import { useRef, useState } from 'react';
import type React from 'react';
import { AlertTriangle, FileWarning, Upload } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { parseOrganogramaFile, type ImportParseResult } from '@/lib/organogramaImport';

interface ImportOrganogramaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (result: ImportParseResult) => void;
}

export function ImportOrganogramaDialog({ open, onOpenChange, onConfirm }: ImportOrganogramaDialogProps) {
  const [result, setResult] = useState<ImportParseResult | null>(null);
  const [fileName, setFileName] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setResult(null);
    setFileName('');
    setError(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError(null);
    setIsParsing(true);
    try {
      const parsed = await parseOrganogramaFile(file);
      setResult(parsed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível ler o arquivo.');
      setResult(null);
    } finally {
      setIsParsing(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleConfirm = () => {
    if (!result) return;
    onConfirm(result);
    handleOpenChange(false);
  };

  const totalDepartamentos = result?.grupos.reduce((sum, g) => sum + g.departamentos.length, 0) ?? 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Importar estrutura</DialogTitle>
          <DialogDescription>
            Envie uma planilha (.csv, .xlsx, .xls) ou XML com a estrutura de departamentos e pessoas. O sistema
            interpreta o arquivo e mostra abaixo o que será adicionado antes de confirmar — nada é salvo ainda.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border p-8 text-center">
            <Upload className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Formatos aceitos: CSV, Excel (.xlsx/.xls) ou XML.
            </p>
            <Button onClick={() => inputRef.current?.click()} disabled={isParsing}>
              {isParsing ? 'Lendo arquivo...' : 'Escolher arquivo'}
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.xls,.xml"
              className="hidden"
              onChange={handleFileChange}
            />
            {error && (
              <p className="flex items-center gap-1.5 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4" /> {error}
              </p>
            )}
          </div>
        )}

        {result && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between rounded-md bg-muted px-3 py-2 text-sm">
              <span className="truncate font-medium">{fileName}</span>
              <div className="flex gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary">{result.grupos.length} grupo(s)</Badge>
                <Badge variant="secondary">{totalDepartamentos} departamento(s)</Badge>
                <Badge variant="secondary">{result.totalPessoas} pessoa(s)</Badge>
              </div>
            </div>

            <ScrollArea className="h-72 rounded-md border border-border p-3">
              <div className="flex flex-col gap-4">
                {result.grupos.map((grupo, gi) => (
                  <div key={gi} className="flex flex-col gap-1.5">
                    <p className="text-sm font-semibold">
                      {grupo.titulo}
                      {grupo.coordenador && (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          Coordenador: {grupo.coordenador}
                        </span>
                      )}
                    </p>
                    {grupo.departamentos.map((dep, di) => (
                      <div key={di} className="ml-4 flex flex-col gap-0.5">
                        <p className="text-xs font-medium text-muted-foreground">{dep.nome}</p>
                        {dep.pessoas.map((pessoa, pi) => (
                          <p key={pi} className="ml-4 text-xs">
                            {pessoa.nome}
                            {pessoa.cargo && <span className="text-muted-foreground"> — {pessoa.cargo}</span>}
                            {pessoa.vagaAberta && (
                              <Badge variant="outline" className="ml-2 h-4 px-1 text-[10px] text-amber-600">
                                vaga aberta
                              </Badge>
                            )}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}

                {result.grupos.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Nenhuma estrutura reconhecida neste arquivo.
                  </p>
                )}
              </div>
            </ScrollArea>

            {(result.duplicatas.length > 0 || result.unrecognizedLines.length > 0) && (
              <div className="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <p className="flex items-center gap-1.5 font-medium">
                  <FileWarning className="h-3.5 w-3.5" /> Pontos para revisar
                </p>
                {result.duplicatas.map((dup, i) => (
                  <p key={`dup-${i}`}>
                    Nome duplicado <strong>{dup.nome}</strong> em: {dup.ocorrencias.join('; ')}. Confira se não é a
                    mesma pessoa antes de confirmar.
                  </p>
                ))}
                {result.unrecognizedLines.length > 0 && (
                  <div>
                    <p>
                      {result.unrecognizedLines.length} linha(s) não reconhecida(s) — não serão importadas:
                    </p>
                    <ul className="ml-4 list-disc">
                      {result.unrecognizedLines.slice(0, 8).map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                      {result.unrecognizedLines.length > 8 && <li>e mais {result.unrecognizedLines.length - 8}...</li>}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          {result && (
            <>
              <Button variant="ghost" onClick={reset}>
                Escolher outro arquivo
              </Button>
              <Button onClick={handleConfirm} disabled={result.totalPessoas === 0}>
                Importar {result.totalPessoas} pessoa(s)
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
